import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { getDotPhrase } from "../api/dotphrases";
import { fetchProfile } from "../api/profiles";
import {
  fetchUserSnippets,
  incrementSnippetUsage,
} from "../api/snippets";
import {
  DEFAULT_SUGGESTION_FONT_SIZE,
  DEFAULT_SUGGESTION_HIGHLIGHT,
  getSuggestionFontSizeOption,
  getSuggestionHighlightOption,
} from "../constants/profileOptions";
import { useLanguage } from "../i18n/LanguageContext";

const scoreSuggestion = (query, text, timesUsed) => {
  const q = query.toLocaleLowerCase();
  const t = text.toLocaleLowerCase();
  const resemblance = q.length / Math.max(t.length, 1);
  const usageBoost = Math.log1p(timesUsed ?? 0) * 0.15;
  return resemblance + usageBoost;
};

const capitalizeFirstLetter = (text, firstLetterCapitalized) => {
  if (!text) return text;
  if (firstLetterCapitalized) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
  return text.charAt(0).toLowerCase() + text.slice(1);
};

const expandDotphrase = async (text, cursorPosition) => {
  const safeCursor = Math.max(0, Math.min(cursorPosition, text.length));
  const before = text.slice(0, safeCursor);
  const after = text.slice(safeCursor);
  const match = before.match(/(?:^|\s)(\.[^\s]{3,})$/);
  if (!match) return null;

  const token = match[1];
  const abbr = token.slice(1);
  const dotPhrase = await getDotPhrase(abbr);
  if (!dotPhrase?.text) return null;

  const tokenStart = before.length - token.length;
  const nextText = text.slice(0, tokenStart) + dotPhrase.text + after;
  return {
    text: nextText,
    cursorPosition: tokenStart + dotPhrase.text.length,
  };
};

export const TryOutPage = () => {
  const { portal } = useLanguage();
  const copy = portal.playground;
  const textareaRef = useRef(null);
  const mirrorRef = useRef(null);
  const queryRef = useRef("");
  const [note, setNote] = useState("");
  const [snippets, setSnippets] = useState([]);
  const [highlightId, setHighlightId] = useState(DEFAULT_SUGGESTION_HIGHLIGHT);
  const [fontSizeId, setFontSizeId] = useState(DEFAULT_SUGGESTION_FONT_SIZE);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [caretPosition, setCaretPosition] = useState({ top: 0, left: 0 });
  const [cursor, setCursor] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const highlightStyle = useMemo(
    () => getSuggestionHighlightOption(highlightId),
    [highlightId]
  );
  const fontSizeStyle = useMemo(
    () => getSuggestionFontSizeOption(fontSizeId),
    [fontSizeId]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [profile, nextSnippets] = await Promise.all([
        fetchProfile(),
        fetchUserSnippets(),
      ]);
      setHighlightId(
        profile?.suggestion_highlight || DEFAULT_SUGGESTION_HIGHLIGHT
      );
      setFontSizeId(
        profile?.suggestion_font_size || DEFAULT_SUGGESTION_FONT_SIZE
      );
      setSnippets(Array.isArray(nextSnippets) ? nextSnippets : []);
    } catch (err) {
      setError(err?.message || copy.loadFailed);
      setSnippets([]);
    } finally {
      setLoading(false);
    }
  }, [copy.loadFailed]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateSuggestions = useCallback(
    (query) => {
      if (query.length <= 1) {
        setSuggestions([]);
        return;
      }

      const firstLetterCapitalized =
        query.charAt(0).toUpperCase() === query.charAt(0);
      const queryLower = query.toLocaleLowerCase();
      const next = snippets
        .filter((snippet) =>
          (snippet.text || "").toLocaleLowerCase().startsWith(queryLower)
        )
        .sort((a, b) => {
          const scoreDiff =
            scoreSuggestion(query, b.text, b.times_used ?? 0) -
            scoreSuggestion(query, a.text, a.times_used ?? 0);
          if (scoreDiff !== 0) return scoreDiff;
          return a.text.length - b.text.length;
        })
        .slice(0, 10)
        .map((snippet) => ({
          _id: snippet._id,
          text: capitalizeFirstLetter(snippet.text, firstLetterCapitalized),
        }));

      setSuggestions(next);
      setHighlightedIndex(0);
    },
    [snippets]
  );

  const getCaretCoordinates = useCallback(() => {
    const textarea = textareaRef.current;
    const mirror = mirrorRef.current;
    if (!textarea || !mirror) return;

    const computed = window.getComputedStyle(textarea);
    const rect = textarea.getBoundingClientRect();

    mirror.style.fontFamily = computed.fontFamily;
    mirror.style.fontSize = computed.fontSize;
    mirror.style.fontWeight = computed.fontWeight;
    mirror.style.fontStyle = computed.fontStyle;
    mirror.style.letterSpacing = computed.letterSpacing;
    mirror.style.textTransform = computed.textTransform;
    mirror.style.padding = computed.padding;
    mirror.style.border = computed.border;
    mirror.style.width = `${textarea.clientWidth}px`;
    mirror.style.whiteSpace = "pre-wrap";
    mirror.style.wordBreak = computed.wordBreak;
    mirror.style.lineHeight = computed.lineHeight;
    mirror.style.boxSizing = computed.boxSizing;
    mirror.style.visibility = "hidden";
    mirror.style.position = "fixed";
    mirror.style.top = `${rect.top}px`;
    mirror.style.left = `${rect.left}px`;
    mirror.style.height = `${textarea.clientHeight}px`;
    mirror.style.overflow = "hidden";
    mirror.style.pointerEvents = "none";
    mirror.style.zIndex = "-1";

    const selectionStart = textarea.selectionStart ?? 0;
    mirror.textContent = (textarea.value || "").substring(0, selectionStart);
    mirror.scrollTop = textarea.scrollTop;
    mirror.scrollLeft = textarea.scrollLeft;

    const marker = document.createElement("span");
    marker.textContent = "\u200b";
    mirror.appendChild(marker);
    const markerRect = marker.getBoundingClientRect();
    mirror.removeChild(marker);

    const lineHeight = Number.parseFloat(computed.lineHeight);
    const fontSize = Number.parseFloat(computed.fontSize);
    const effectiveLineHeight = Number.isFinite(lineHeight)
      ? lineHeight
      : Number.isFinite(fontSize)
        ? fontSize * 1.2
        : 16;

    const wrapRect = textarea.parentElement?.getBoundingClientRect();
    setCaretPosition({
      top: markerRect.top + effectiveLineHeight + 2 - (wrapRect?.top || 0),
      left: markerRect.left - (wrapRect?.left || 0),
    });
  }, []);

  const selectSuggestion = useCallback(
    (suggestion) => {
      const textarea = textareaRef.current;
      if (!textarea || !suggestion) return;

      const currentText = textarea.value;
      const selectionStart = textarea.selectionStart ?? cursor;
      const beforeCursor = currentText.slice(0, selectionStart);
      const afterCursor = currentText.slice(selectionStart);
      const query = queryRef.current;
      const beforeQuery = beforeCursor.slice(
        0,
        beforeCursor.length - query.length
      );
      const nextText = beforeQuery + suggestion.text + afterCursor;
      const nextCaret = beforeQuery.length + suggestion.text.length;

      textarea.value = nextText;
      textarea.selectionStart = nextCaret;
      textarea.selectionEnd = nextCaret;
      setNote(nextText);
      setCursor(nextCaret);
      setSuggestions([]);
      queryRef.current = "";

      setSnippets((prev) =>
        prev.map((snippet) =>
          snippet._id === suggestion._id
            ? { ...snippet, times_used: (snippet.times_used ?? 0) + 1 }
            : snippet
        )
      );
      void incrementSnippetUsage(suggestion._id).catch(() => { });
      requestAnimationFrame(getCaretCoordinates);
    },
    [cursor, getCaretCoordinates]
  );

  const handleInput = async (event) => {
    const textarea = event.target;
    const value = textarea.value;
    const selectionStart = textarea.selectionStart ?? value.length;

    setNote(value);
    setCursor(selectionStart);
    getCaretCoordinates();

    const textBeforeCursor = value.slice(0, selectionStart);
    const lastLine = textBeforeCursor.split("\n").pop() || "";
    const words = lastLine.trimEnd().split(/\s+/).filter(Boolean);
    let query = "";
    for (let i = 0; i < words.length; i += 1) {
      const candidate = words.slice(i).join(" ");
      const hasMatch = snippets.some((snippet) =>
        (snippet.text || "")
          .toLocaleLowerCase()
          .startsWith(candidate.toLocaleLowerCase())
      );
      if (hasMatch) {
        query = candidate;
        break;
      }
    }
    if (!query && words.length > 0) query = words[words.length - 1];
    queryRef.current = query;
    updateSuggestions(query);

    const replacement = await expandDotphrase(value, selectionStart);
    if (replacement) {
      textarea.value = replacement.text;
      textarea.selectionStart = replacement.cursorPosition;
      textarea.selectionEnd = replacement.cursorPosition;
      setNote(replacement.text);
      setCursor(replacement.cursorPosition);
      setSuggestions([]);
      queryRef.current = "";
      getCaretCoordinates();
    }
  };

  const handleKeyDown = (event) => {
    if (suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex(
        (prev) => (prev - 1 + suggestions.length) % suggestions.length
      );
    } else if (event.key === "Tab") {
      event.preventDefault();
      selectSuggestion(suggestions[highlightedIndex]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setSuggestions([]);
    }
  };

  useLayoutEffect(() => {
    getCaretCoordinates();
  }, [note, suggestions, getCaretCoordinates]);

  return (
    <AppShell title={copy.title} description={copy.description}>
      {error && <div className="app-alert">{error}</div>}

      <section className="app-form-card">
        <div className="try-note-wrap" style={{ marginTop: 16 }}>
          <textarea
            ref={textareaRef}
            className="try-note"
            value={note}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            onClick={getCaretCoordinates}
            onKeyUp={getCaretCoordinates}
            onScroll={getCaretCoordinates}
            placeholder={copy.placeholder}
            disabled={loading}
            aria-label={copy.ariaLabel}
          />
          <div ref={mirrorRef} aria-hidden="true" />
          {suggestions.length > 0 && (
            <div
              className="try-suggestions"
              style={{
                top: caretPosition.top,
                left: caretPosition.left,
              }}
              role="listbox"
            >
              {suggestions.map((suggestion, index) => (
                <button
                  key={suggestion._id}
                  type="button"
                  className={
                    index === highlightedIndex
                      ? "try-suggestion highlighted"
                      : "try-suggestion"
                  }
                  style={{
                    fontSize: `${fontSizeStyle.px}px`,
                    ...(index === highlightedIndex
                      ? {
                          backgroundColor: highlightStyle.bgcolor,
                          outline: `1px solid ${highlightStyle.borderColor}`,
                          outlineOffset: "-1px",
                        }
                      : {}),
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectSuggestion(suggestion);
                  }}
                  role="option"
                  aria-selected={index === highlightedIndex}
                >
                  <span>{suggestion.text}</span>
                  {index === highlightedIndex && (
                    <span className="try-tab-hint">{copy.tabHint}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <ul className="try-instructions" style={{ marginTop: 16 }}>
          <li>
            {copy.instruction1Before}{" "}
            <Link to="/app/snippets">{portal.nav.snippets}</Link>{" "}
            {copy.instruction1After}{" "}
            <strong>{copy.instruction1Tab}</strong> {copy.instruction1End}
          </li>
          <li>
            {copy.instruction2Before} <code>.hpi</code>{" "}
            {copy.instruction2After}
          </li>
          <li>
            {copy.instruction3Before}{" "}
            <Link to="/app/dotphrases">{portal.nav.dotphrases}</Link>{" "}
            {copy.instruction3Middle}{" "}
            <Link to="/app/library">{portal.nav.library}</Link>
            {copy.instruction3End}
          </li>
        </ul>
      </section>
    </AppShell>
  );
};
